import { Fragment, useCallback, useEffect, useMemo } from 'react';
import carService from '~/assets/icons/car_service.svg';
import category from '~/assets/icons/category.svg';
import { ShopCategoriesGrid } from '~/components/ui/Shop';
import {
	ShopCategory,
	useShopCategoryControls,
} from '~/components/ui/ShopCategory';
import { rage, useObservable } from '~/rage';
import { VehicleConsumableGroup } from '~/shared';
import { useRepair } from '../../hooks';
import {
	setActiveCategory,
	setActiveStage,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { SectionHeading } from '../SectionHeading';
import { VehicleState } from '../VehicleState';
import { categories, getCategoryByType } from './CategoryTab.model';
import { StyledSection } from './CategoryTab.styled';

const CategoryTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const activeCategory = useLayoutSelector(
		(state) => state.vehicleService.activeCategory,
	);
	const { handleRepair } = useRepair();
	const modalState = useObservable(rage.modal.getCurrentModal());
	const consumables = useObservable(
		rage.vehicleServiceStation.getConsumables(),
	);

	const categoryUnseenMap = useMemo(() => {
		const map: Partial<Record<VehicleConsumableGroup | 'engine', boolean>> = {};

		for (const category of categories) {
			if (category.type === 'engine') {
				continue;
			}

			const categoryItems = consumables?.filter(
				(item) => item.group === category.type,
			);

			if (!categoryItems || categoryItems.length === 0) {
				continue;
			}

			for (const item of categoryItems) {
				const seenTrackerKey = `${category.type}.${item.id}`;
				if (rage.vehicleServiceStation.isUnseen(seenTrackerKey)) {
					map[category.type as VehicleConsumableGroup] = true;
					break;
				}
			}
		}

		return map;
	}, [consumables]);

	const handleSelect = useCallback((index: number) => {
		if (categories?.[index] === undefined) {
			return;
		}

		if (categories?.[index]?.type === 'engine') {
			handleRepair();
			return;
		}

		dispatch(setActiveStage(StageTypes.List));
		dispatch(setActiveCategory(categories[index].type));
	}, []);

	const { setMaxCategoryIndex, activeCategoryIndex, setActiveCategoryIndex } =
		useShopCategoryControls(
			activeCategory
				? categories.map((item) => item.type).indexOf(activeCategory)
				: null,
			handleSelect,
			!!modalState,
		);

	useEffect(() => {
		if (activeCategoryIndex === null) {
			dispatch(setActiveCategory(null));
			return;
		}

		dispatch(setActiveCategory(categories[activeCategoryIndex].type));
	}, [activeCategoryIndex]);

	useEffect(() => {
		setMaxCategoryIndex(categories.length);
	}, []);

	return (
		<Fragment>
			<StyledSection style={{ marginTop: 30 }}>
				<SectionHeading text={'Состояние ТС'} icon={carService} />
				<VehicleState />
			</StyledSection>
			<StyledSection style={{ marginTop: 30 }}>
				<SectionHeading text={'Категории товаров'} icon={category} />
				<ShopCategoriesGrid>
					<ShopCategory
						name={categories[0].name}
						icon={categories[0].image}
						isActive={getCategoryByType(activeCategory)?.id === 0}
						onClick={() => handleSelect(0)}
						onMouseEnter={() => setActiveCategoryIndex(0)}
						onMouseLeave={() => setActiveCategoryIndex(null)}
						bgColor={'#1F8034'}
					/>
					{categories
						.filter((item) => item.type !== 'engine')
						.map((item, index) => {
							const seenTrackerKey = `${item.type}`;
							const isNew =
								categoryUnseenMap[item.type as VehicleConsumableGroup] ??
								rage.vehicleServiceStation.isUnseen(seenTrackerKey);

							return (
								<ShopCategory
									name={item.name}
									icon={item.image}
									onClick={() => handleSelect(index + 1)}
									isActive={getCategoryByType(activeCategory)?.id === index + 1}
									isNew={isNew}
									onMouseEnter={() => setActiveCategoryIndex(index + 1)}
									onMouseLeave={() => setActiveCategoryIndex(null)}
									key={index}
								/>
							);
						})}
				</ShopCategoriesGrid>
			</StyledSection>
		</Fragment>
	);
};

export { CategoryTab };
